
import { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useSharedFolderHandling } from "./useSharedFolderHandling";
import { supabase } from "@/integrations/supabase/client";

export const useFolderHandling = (projectId: string | null) => {
  const navigate = useNavigate();
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [folderTreeKey, setFolderTreeKey] = useState<number>(0);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const { isSharedFolder, setIsSharedFolder, fetchProjectIdForSharedFolder } = useSharedFolderHandling();

  /**
   * Handle selecting a folder
   */
  const handleSelectFolder = useCallback(async (folderId: string) => {
    console.log("Selected folder:", folderId, "Project ID:", projectId);
    
    try {
      // Verify folder exists before navigating
      const { data: folderData, error: folderError } = await supabase
        .from("folders")
        .select("id, project_id, name")
        .eq("id", folderId)
        .single();

      if (folderError) {
        console.error("Error fetching folder:", folderError);
        toast.error("Kunde inte öppna mappen", { 
          description: "Mappen kunde inte hittas eller du har inte behörighet." 
        });
        return;
      }

      console.log("Folder data:", folderData);
      setSelectedFolderId(folderId);
      
      // Use project ID from the folder if current projectId is null
      const effectiveProjectId = projectId || folderData?.project_id;
      
      if (effectiveProjectId) {
        setIsSharedFolder(false); // This is a project-owned folder
        navigate(`/uppdrag/${effectiveProjectId}/${folderId}`);
      } else {
        // If no project id, it might be a shared folder
        setIsSharedFolder(true); // This is a shared folder
        navigate(`/uppdrag/shared/${folderId}`);
      }
    } catch (error) {
      console.error("Error in handleSelectFolder:", error);
      toast.error("Ett fel uppstod när mappen skulle öppnas");
    }
  }, [projectId, setIsSharedFolder]);

  /**
   * Handle selecting a shared folder
   */
  const handleSelectSharedFolder = useCallback(async (folderId: string) => {
    console.log("Selected shared folder:", folderId);
    setIsSharedFolder(true);
    setSelectedFolderId(folderId);
    navigate(`/uppdrag/shared/${folderId}`);
    return folderId;
  }, [setIsSharedFolder, navigate]);

  /**
   * Handle creating a root folder - now just returns true to indicate ready for creation
   */
  const handleCreateRootFolder = useCallback(() => {
    // Check if there's a project selected
    if (!projectId) {
      toast.error("Välj eller skapa ett projekt först för att kunna skapa mappar.", { 
        description: "Du behöver ett aktivt projekt för att organisera dina riskbedömningar." 
      });
      return false;
    }
    
    // Return true to indicate we're ready for folder creation
    return true;
  }, [projectId]);
  
  /**
   * Handle navigating back to folders
   * This internal implementation accepts the isSharedFolder parameter
   * but the public API doesn't require it for compatibility with component props
   */
  const handleBackToFoldersInternal = useCallback((isSharedFolderValue: boolean) => {
    setSelectedFolderId(null);
    if (isSharedFolderValue) {
      navigate(`/uppdrag`);
      setIsSharedFolder(false);
    } else if (projectId) {
      navigate(`/uppdrag/${projectId}`);
      setIsSharedFolder(false);
    } else {
      navigate(`/uppdrag`);
      setIsSharedFolder(false);
    }
  }, [projectId, setIsSharedFolder, navigate]);

  /**
   * Handle creating egenkontroll
   */
  const handleCreateEgenkontroll = useCallback((folderId: string) => {
    console.log("Creating egenkontroll for folder:", folderId, "Project ID:", projectId);
    
    if (!projectId) {
      // For shared folders, try to fetch the project ID first
      fetchProjectIdForSharedFolder(folderId).then(fetchedProjectId => {
        if (fetchedProjectId) {
          console.log("Found project ID for shared folder:", fetchedProjectId);
          navigate(`/egenkontroll?projektId=${fetchedProjectId}&folderId=${folderId}`);
        } else {
          toast.error("Kunde inte hitta projekt-ID för denna mapp");
        }
      });
      return;
    }
    
    // If we have a project ID, use it directly
    navigate(`/egenkontroll?projektId=${projectId}&folderId=${folderId}`);
  }, [projectId, fetchProjectIdForSharedFolder, navigate]);

  // Public API for backward compatibility
  const handleBackToFolders = useCallback(() => {
    handleBackToFoldersInternal(false);
  }, [handleBackToFoldersInternal]);

  // Refresh folder tree to update UI after changes
  const refreshFolderTree = useCallback(() => {
    console.log("Refreshing folder tree...");
    setFolderTreeKey(prev => prev + 1);
    setRefreshTrigger(prev => prev + 1);
  }, []);

  return {
    selectedFolderId,
    setSelectedFolderId,
    handleSelectFolder,
    handleSelectSharedFolder,
    handleCreateRootFolder,
    handleBackToFolders,
    handleBackToFoldersInternal,
    handleCreateEgenkontroll,
    isSharedFolder,
    setIsSharedFolder,
    folderTreeKey,
    refreshTrigger,
    fetchProjectIdForSharedFolder,
    refreshFolderTree
  };
};
